// File: components/ComponentRenderer.tsx
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import TextComponent from "./TextComponent";
import parse from "html-react-parser";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import TabletOutlinedIcon from "@mui/icons-material/TabletOutlined";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import WidgetsIcon from "@mui/icons-material/Widgets";
import { Stack } from "@mui/system";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// Component Renderer
const ComponentRenderer = ({
  component,
  selectedId,
  onSelect,
  onUpdate,
  theme,
  pageDictionary,
  setIsTextEditing,
  isTextEditing,
  setUnsavedText,
}: any) => {
  const isSelected = selectedId === component?.id;

  switch (component.type) {
    case "text":
      return (
        <Box>
          <TextComponent
            component={component}
            isSelected={isSelected}
            onSelect={onSelect}
            onUpdate={onUpdate}
            theme={theme}
            pageDictionary={pageDictionary}
            setIsTextEditing={setIsTextEditing}
            isTextEditing={isTextEditing === component.id}
            setUnsavedText={setUnsavedText}
          />
        </Box>
      );

    case "widgets":
      const className = component?.props?.className || "";
      let DeviceIcon = null;
      let deviceLabel = "";

      if (className.includes("mobile")) {
        DeviceIcon = SmartphoneIcon;
        deviceLabel = "Mobile";
      } else if (className.includes("tablet")) {
        DeviceIcon = TabletOutlinedIcon;
        deviceLabel = "Tablet";
      } else if (className.includes("desktop")) {
        DeviceIcon = DesktopWindowsOutlinedIcon;
        deviceLabel = "Desktop";
      }

      return (
        <Paper
          elevation={isSelected ? 4 : 1}
          className={component.props.className}
          id={`component-${component.id}`}
          data-device={component.props.className?.split(" ").pop()}
          tabIndex={-1}
          sx={{
            cursor: "pointer",
            padding: 2,
            marginY: 1.5,
            border: isSelected ? "2px solid #9C27B080" : "1px solid #e0e0e0",
            borderRadius: 2,
            backgroundColor: component.props.backgroundColor || "#fff",
            transition: "box-shadow 0.2s ease-in-out",
            "&:hover": {
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            },
            position: "relative",
            marginBottom: "24px",
            boxShadow: `
      0px 2px 1px -1px #9C27B033,
      0px 1px 1px 0px #9C27B024,
      0px 1px 3px 0px #9C27B01F
    `,
          }}
          onClick={() => onSelect(component.id)}
        >
          <Stack
            flexDirection={"column"}
            justifyContent={"space-between"}
            mb={2}
          >
            <Stack
              gap={1}
              alignItems="center"
              flexDirection={"row"}
              justifyContent={"space-between"}
              mb={2}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{ color: "#000000DE" }}
              >
                <WidgetsIcon sx={{ width: 16, height: 16 }} />
                <Typography sx={{ fontWeight: 500 }}>Widget</Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ color: "#000000DE" }}
              >
                <Typography sx={{ fontWeight: 500 }}>Available On:</Typography>
                {DeviceIcon && (
                  <Tooltip title={`Device: ${deviceLabel}`}>
                    <DeviceIcon fontSize="small" />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            {component?.props?.className.includes("internal-widget desktop") ? (
              <Box>{parse(component?.props?.text)}</Box>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  fontSize: "14px",
                  color: "#5F2120", // dark red
                  backgroundColor: "#ffebee", // light red background
                  borderRadius: "4px",
                  padding: "6px 12px",
                  fontWeight: 500,
                  width: "100%",
                }}
              >
                <ErrorOutlineIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                {parse(component?.props?.text)}
              </Stack>
            )}
          </Stack>

          <Typography variant="subtitle1" gutterBottom>
            {`${component?.props?.attributes?.widget_type} : ${component?.props?.attributes?.widget_name}` ||
              ""}
          </Typography>
        </Paper>
      );

    default:
      return null;
  }
};

export default ComponentRenderer;
